/**
 * Hourly marketplace pre-search wizard: package / pattern / slot helpers.
 * Package keys match CareAttendantBookingDialog + CareHubBookingServiceStep.
 */

import { buildServiceSelection } from "./careHubBookingConfig";
import { CARE_ATTENDANT_HOURLY_PACKAGES } from "./nemaHourlyServiceList";
import { isSlotPastNow } from "../utils/dateFormat";
import {
  formatCareHubSlotLabel,
  getFixedBlocksForDuration,
  isFixedBlockCoveredBySlots,
} from "./careHubRosterConfig";
import {
  buildRecurringOccurrences,
  recurringPatternNeedsDatePicker,
} from "../utils/careHubBookingRecurrence";

export const HOURLY_SEARCH_PATTERNS = [
  {
    id: "instant",
    badge: "Express · 60 min",
    badgeTone: "express",
    title: "Instant booking",
    description: "Dispatches an active caregiver to arrive within 1 hour.",
  },
  {
    id: "single",
    badge: "Scheduled",
    badgeTone: "scheduled",
    title: "One time",
    description: "Book a custom date and time slot in advance.",
  },
  {
    id: "recurring",
    badge: "Recurring",
    badgeTone: "subscription",
    title: "Recurring",
    description: "Auto-reserves future slots daily, weekly, or on alternate days.",
  },
];

export const HOURLY_RECURRING_OPTIONS = [
  { id: "daily", label: "Daily" },
  { id: "alternate", label: "Alternate Days" },
  { id: "weekly", label: "Weekly" },
  { id: "weekdays", label: "Weekdays" },
  { id: "custom", label: "Custom Dates" },
];

export const DEFAULT_HOURLY_PACKAGE_KEY = "hygiene_personal";
export const DEFAULT_HOURLY_TIER_ID = "t2";
export const DEFAULT_HOURLY_PATTERN = "single";

export function isHourlyMarketplaceShift(searchShift) {
  return String(searchShift || "").toLowerCase() === "hourly";
}

export function resolveHourlyPackageKey(packageKey) {
  const key = String(packageKey || "").trim();
  if (!key) return DEFAULT_HOURLY_PACKAGE_KEY;
  if (key === "hygiene_personal" || key.startsWith("companion:")) return key;
  return DEFAULT_HOURLY_PACKAGE_KEY;
}

export function tierIdFromHourlyPackageKey(packageKey) {
  const key = resolveHourlyPackageKey(packageKey);
  if (key.startsWith("companion:")) return key.split(":")[1] || DEFAULT_HOURLY_TIER_ID;
  return DEFAULT_HOURLY_TIER_ID;
}

export function resolveHourlyServiceSelection({
  packageKey = DEFAULT_HOURLY_PACKAGE_KEY,
  serviceTab = "attendant",
} = {}) {
  const role =
    serviceTab === "nurses" || serviceTab === "physio" ? serviceTab : "attendant";
  const key = resolveHourlyPackageKey(packageKey);
  const tierId = tierIdFromHourlyPackageKey(key);
  return buildServiceSelection({
    role,
    packageKey: key,
    tierId,
    searchShift: "hourly",
  });
}

export function listHourlyPackageChoices() {
  return CARE_ATTENDANT_HOURLY_PACKAGES.flatMap((pack) => {
    if (pack.pricingMode === "tiers") {
      return (pack.tiers || []).map((tier) => ({
        key: `companion:${tier.id}`,
        title: `Companion Service (${tier.label})`,
        durationLabel: tier.label,
        badge: pack.servicePack || "Service Pack 2",
        tasks: pack.tasks || [],
        defaultPriceInr: tier.defaultPriceInr || 500,
        tierId: tier.id,
      }));
    }
    return [
      {
        key: pack.id,
        title: pack.title || "Hygiene Care & Personal Assistance",
        durationLabel: pack.durationLabel || "1.5 - 2 hrs",
        badge: pack.servicePack || "Service Pack 1",
        tasks: pack.tasks || [],
        defaultPriceInr: pack.defaultPriceInr || 500,
      },
    ];
  });
}

function buildSlotKeySet(matches = []) {
  const slotSet = new Set();
  for (const match of matches) {
    for (const slot of match?.slots || []) {
      const start = String(slot.startTime || slot.start || "").slice(0, 5);
      const end = String(slot.endTime || slot.end || "").slice(0, 5);
      if (!start || !end) continue;
      slotSet.add(`${start}-${end}`);
    }
  }
  return slotSet;
}

/** Collect customer-facing slot chips from matchmaker matches (hourly packages). */
export function collectHourlySlotsFromMatches(matches = [], { durationMinutes = 120, preferRawSlots = false } = {}) {
  const slotSet = buildSlotKeySet(matches);
  if (preferRawSlots) {
    return [...slotSet]
      .map((key) => {
        const [start, end] = String(key).split("-");
        if (!start || !end) return null;
        return {
          id: key,
          start,
          end,
          label: formatCareHubSlotLabel(start, end),
          available: true,
        };
      })
      .filter(Boolean)
      .sort((a, b) => a.start.localeCompare(b.start));
  }

  return getFixedBlocksForDuration(durationMinutes, "hourly")
    .filter(
      (block) =>
        slotSet.has(`${block.start}-${block.end}`) ||
        isFixedBlockCoveredBySlots(block.start, block.end, slotSet)
    )
    .map((block) => ({
      id: `${block.start}-${block.end}`,
      start: block.start,
      end: block.end,
      label: block.label || formatCareHubSlotLabel(block.start, block.end),
      available: true,
    }));
}

export function matchHasHourlySlot(match, selectedSlot) {
  if (!selectedSlot?.start) return false;
  const start = String(selectedSlot.start).slice(0, 5);
  const end = String(selectedSlot.end || selectedSlot.start).slice(0, 5);
  const slotSet = buildSlotKeySet([match]);
  if (slotSet.has(`${start}-${end}`)) return true;
  return isFixedBlockCoveredBySlots(start, end, slotSet);
}

export function extractMatchmakerMatches(res) {
  if (Array.isArray(res?.matches)) return res.matches;
  if (Array.isArray(res?.data?.matches)) return res.data.matches;
  return [];
}

export function normalizeHourlySlot(slot) {
  if (!slot) return null;
  const start = String(slot.start || slot.startTime || "").slice(0, 5);
  if (!start) return null;
  const end = String(slot.end || slot.endTime || slot.start || slot.startTime || "").slice(0, 5);
  return { ...slot, start, end };
}

/**
 * True when a wall-clock occurrence is already in the past, evaluated in the app's
 * configured timezone (Asia/Kolkata) — not the visitor's own browser/OS timezone.
 */
export function isHourlyOccurrencePast(serviceDate, startTime, now = new Date()) {
  const d = String(serviceDate || "").slice(0, 10);
  const t = String(startTime || "").slice(0, 5);
  if (!/^\d{4}-\d{2}-\d{2}$/.test(d) || !/^\d{2}:\d{2}$/.test(t)) return false;
  return isSlotPastNow(d, t, now);
}

function matchGenderOk(match, genderFilter) {
  if (!genderFilter) return true;
  return (
    String(match?.gender || "")
      .trim()
      .toLowerCase() === genderFilter
  );
}

/** Strict filter — only caregivers the matchmaker returned for the selected slot. */
export function filterMatchesForHourlySlot(matches, slot, { genderFilter = null } = {}) {
  const normalizedSlot = normalizeHourlySlot(slot);
  if (!normalizedSlot) return [];
  return (Array.isArray(matches) ? matches : []).filter(
    (m) => matchGenderOk(m, genderFilter) && matchHasHourlySlot(m, normalizedSlot)
  );
}

/** Booking dates for hourly marketplace (before a slot is chosen). */
export function resolveHourlyMarketplaceDates({
  pattern = "single",
  recurringPattern = "daily",
  searchDate = "",
  selectedDates = [],
  daysServiceRequired = 1,
  weeklyDayOfWeek = 1,
} = {}) {
  const start = String(searchDate || "").slice(0, 10);
  if (!/^\d{4}-\d{2}-\d{2}$/.test(start)) return [];
  if (pattern !== "recurring") return [start];
  if (recurringPatternNeedsDatePicker(recurringPattern)) {
    const listed = [
      ...new Set(
        (Array.isArray(selectedDates) ? selectedDates : [])
          .map((d) => String(d).slice(0, 10))
          .filter((d) => /^\d{4}-\d{2}-\d{2}$/.test(d))
      ),
    ].sort();
    const cap = Math.max(1, Number(daysServiceRequired) || 1);
    return listed.slice(0, cap);
  }
  return buildRecurringOccurrences({
    recurringPattern,
    startDate: start,
    startTime: "08:00",
    endTime: "10:00",
    selectedDates,
    weeklyDayOfWeek,
    count: Math.max(1, Number(daysServiceRequired) || 1),
  })
    .map((o) => String(o?.serviceDate || "").slice(0, 10))
    .filter((d) => /^\d{4}-\d{2}-\d{2}$/.test(d));
}

/**
 * Matchmaker for each booking date; return slot chips free on ALL dates (recurring).
 * Uses existing matchmaker — intersects open slot windows across dates.
 */
export async function fetchHourlySlotsForAllDates({
  listCareHubMatchmaker,
  dates = [],
  matchmakerBaseParams = {},
  durationMinutes = 120,
  preferRawSlots = false,
  filterPackage = null,
  genderFilter = null,
} = {}) {
  if (typeof listCareHubMatchmaker !== "function") return { matches: [], slots: [] };
  const uniqueDates = [
    ...new Set(
      (Array.isArray(dates) ? dates : [])
        .map((d) => String(d || "").slice(0, 10))
        .filter((d) => /^\d{4}-\d{2}-\d{2}$/.test(d))
    ),
  ];
  if (!uniqueDates.length) return { matches: [], slots: [] };

  let intersectionKeys = null;
  let intersectionIds = null;
  const rowByCaregiverId = new Map();

  for (const date of uniqueDates) {
    const res = await listCareHubMatchmaker({ ...matchmakerBaseParams, date });
    let rows = extractMatchmakerMatches(res);
    if (typeof filterPackage === "function") {
      rows = rows.filter(filterPackage);
    }
    if (genderFilter) {
      rows = rows.filter((m) => matchGenderOk(m, genderFilter));
    }
    for (const m of rows) {
      const id = String(m.caregiverId || m.id || "").trim();
      if (id && !rowByCaregiverId.has(id)) rowByCaregiverId.set(id, m);
    }
    const ids = new Set(rows.map((m) => String(m.caregiverId || m.id || "")).filter(Boolean));
    const keys = buildSlotKeySet(rows);
    if (intersectionKeys === null) {
      intersectionKeys = keys;
      intersectionIds = ids;
    } else {
      intersectionKeys = new Set([...intersectionKeys].filter((k) => keys.has(k)));
      intersectionIds = new Set([...intersectionIds].filter((id) => ids.has(id)));
    }
    if (!intersectionKeys.size || !intersectionIds.size) return { matches: [], slots: [] };
  }

  const filteredMatches = [...intersectionIds]
    .map((id) => rowByCaregiverId.get(id))
    .filter(Boolean)
    .map((m) => ({
      ...m,
      slots: (Array.isArray(m.slots) ? m.slots : []).filter((s) => {
        const start = String(s.startTime || s.start || "").slice(0, 5);
        const end = String(s.endTime || s.end || "").slice(0, 5);
        if (!start || !end) return false;
        return (
          intersectionKeys.has(`${start}-${end}`) ||
          isFixedBlockCoveredBySlots(start, end, intersectionKeys)
        );
      }),
    }))
    .filter((m) => m.slots.length);

  const slots = collectHourlySlotsFromMatches(filteredMatches, {
    durationMinutes,
    preferRawSlots,
  }).filter((slot) => {
    const start = String(slot.start || "").slice(0, 5);
    const end = String(slot.end || slot.start || "").slice(0, 5);
    if (
      !intersectionKeys.has(`${start}-${end}`) &&
      !isFixedBlockCoveredBySlots(start, end, intersectionKeys)
    ) {
      return false;
    }
    // Drop times that are already past on the earliest booking date.
    const earliest = uniqueDates.slice().sort()[0];
    return !isHourlyOccurrencePast(earliest, start);
  });

  return { matches: filteredMatches, slots };
}

/** Occurrence rows for hourly marketplace search (single or recurring). */
export function resolveHourlyMarketplaceOccurrences({
  pattern = "single",
  recurringPattern = "daily",
  searchDate = "",
  slot = null,
  selectedDates = [],
  daysServiceRequired = 1,
  weeklyDayOfWeek = 1,
} = {}) {
  const normalized = normalizeHourlySlot(slot);
  if (!searchDate || !normalized?.start) return [];
  if (pattern !== "recurring") {
    return [
      {
        serviceDate: String(searchDate).slice(0, 10),
        startTime: normalized.start,
        endTime: normalized.end,
      },
    ];
  }
  return buildRecurringOccurrences({
    recurringPattern,
    startDate: String(searchDate).slice(0, 10),
    startTime: normalized.start,
    endTime: normalized.end,
    selectedDates: Array.isArray(selectedDates) ? selectedDates : [],
    weeklyDayOfWeek,
    count: Math.max(1, Number(daysServiceRequired) || 1),
  });
}

/**
 * Backend matchmaker for each booking date; return caregivers free for the slot on ALL dates.
 * Uses existing /api/availability/matchmaker — no client-side availability guessing.
 */
export async function fetchMatchesAvailableForOccurrences({
  listCareHubMatchmaker,
  occurrences = [],
  matchmakerBaseParams = {},
  slot = null,
  genderFilter = null,
  filterPackage = null,
  trustBackendSlotFilter = true,
} = {}) {
  if (typeof listCareHubMatchmaker !== "function") return [];
  const normalizedSlot = normalizeHourlySlot(slot);
  if (!normalizedSlot?.start) return [];

  const rows = (Array.isArray(occurrences) ? occurrences : []).filter(
    (o) => o?.serviceDate && (o?.startTime || normalizedSlot.start)
  );
  // Never list caregivers for a schedule that is already unbookable (past first visit).
  if (rows.some((o) => isHourlyOccurrencePast(o.serviceDate, o.startTime || normalizedSlot.start))) {
    return [];
  }

  const uniqueDates = [
    ...new Set(
      rows
        .map((o) => String(o?.serviceDate || "").slice(0, 10))
        .filter((d) => /^\d{4}-\d{2}-\d{2}$/.test(d))
    ),
  ];
  if (!uniqueDates.length) return [];

  let intersectionIds = null;
  const rowByCaregiverId = new Map();

  for (const date of uniqueDates) {
    const res = await listCareHubMatchmaker({
      ...matchmakerBaseParams,
      date,
      startTime: normalizedSlot.start,
      endTime: normalizedSlot.end,
    });
    let matchRows = extractMatchmakerMatches(res);
    if (typeof filterPackage === "function") {
      matchRows = matchRows.filter(filterPackage);
    }
    if (!trustBackendSlotFilter) {
      matchRows = filterMatchesForHourlySlot(matchRows, normalizedSlot, { genderFilter });
    } else if (genderFilter) {
      matchRows = matchRows.filter((m) => matchGenderOk(m, genderFilter));
    }
    for (const m of matchRows) {
      const id = String(m.caregiverId || m.id || "").trim();
      if (id && !rowByCaregiverId.has(id)) rowByCaregiverId.set(id, m);
    }
    const ids = new Set(matchRows.map((m) => String(m.caregiverId || m.id || "")).filter(Boolean));
    if (intersectionIds === null) {
      intersectionIds = ids;
    } else {
      intersectionIds = new Set([...intersectionIds].filter((id) => ids.has(id)));
    }
    if (!intersectionIds.size) return [];
  }

  return [...intersectionIds]
    .map((id) => rowByCaregiverId.get(id))
    .filter(Boolean);
}

/**
 * Live validate one caregiver for all booking occurrences (same APIs as hire/confirm).
 * Throws on first failure.
 */
export function unwrapRecurringAvailabilityResult(res) {
  if (!res || typeof res !== "object") {
    return { ok: false, conflicts: [], occurrences: [] };
  }
  const nested = res.data && typeof res.data === "object" ? res.data : null;
  const body = nested && ("ok" in nested || "conflicts" in nested) ? { ...res, ...nested } : res;
  return {
    ok: Boolean(body.ok),
    conflicts: Array.isArray(body.conflicts) ? body.conflicts : [],
    occurrences: Array.isArray(body.occurrences) ? body.occurrences : [],
  };
}

export async function validateCaregiverAvailableForOccurrences({
  validateAvailabilitySlot,
  validateRecurringAvailability,
  caregiverId,
  occurrences = [],
  durationMinutes,
  serviceId,
  lat,
  lng,
  excludeBookingId,
}) {
  const cgId = String(caregiverId || "").trim();
  if (!cgId) throw new Error("Caregiver is required.");
  const rows = (Array.isArray(occurrences) ? occurrences : []).filter(
    (o) => o?.serviceDate && o?.startTime && o?.endTime
  );
  if (!rows.length) throw new Error("Booking schedule is incomplete.");

  const past = rows.find((o) => isHourlyOccurrencePast(o.serviceDate, o.startTime));
  if (past) {
    const err = new Error(
      `The ${past.serviceDate} ${String(past.startTime).slice(0, 5)} visit has already started or passed. Choose a later start date or time.`
    );
    err.code = "BOOKING_START_TIME_PAST";
    throw err;
  }

  for (const occ of rows) {
    await validateAvailabilitySlot({
      caregiverId: cgId,
      serviceDate: occ.serviceDate,
      startTime: occ.startTime,
      endTime: occ.endTime,
      durationMinutes,
      serviceId,
      lat,
      lng,
      excludeBookingId,
    });
  }

  if (rows.length > 1 && typeof validateRecurringAvailability === "function") {
    const recurringCheck = unwrapRecurringAvailabilityResult(
      await validateRecurringAvailability({
        caregiverId: cgId,
        occurrences: rows,
        durationMinutes,
        serviceId,
        lat,
        lng,
        excludeBookingId,
      })
    );
    if (!recurringCheck.ok) {
      const conflict = recurringCheck.conflicts[0];
      const err = new Error(
        conflict?.message ||
          "This caregiver is not available for all selected dates and times."
      );
      err.code = conflict?.code || "RECURRENCE_CONFLICT";
      throw err;
    }
  }
}

function parseMatchmakerList(value) {
  if (Array.isArray(value)) return value.map((s) => String(s).trim()).filter(Boolean);
  return String(value || "")
    .split(",")
    .map((s) => s.trim())
    .filter(Boolean);
}

export function mapMatchmakerRowToMarketplaceCaregiver(match) {
  if (!match) return null;
  return {
    id: match.caregiverId || match.id,
    caregiverId: match.caregiverId || match.id,
    name: match.name || "Caregiver",
    rating: match.rating != null ? Number(match.rating) : null,
    reviewCount: Number(match.reviewCount) || 0,
    years:
      match.experienceYears != null
        ? Number(match.experienceYears)
        : match.years != null
          ? Number(match.years)
          : null,
    distanceKm: match.distanceKm != null ? Number(match.distanceKm) : null,
    skills: parseMatchmakerList(match.skills),
    languages: parseMatchmakerList(match.languages),
    gender: match.gender || null,
    age: match.age != null ? Number(match.age) : null,
    height: match.height || null,
    weight: match.weight || null,
    profileImageUrl: match.profileImageUrl || match.avatarUrl || "",
    hourlyRateExpectation:
      match.estimatedCaregiverFeeInr != null
        ? Number(match.estimatedCaregiverFeeInr)
        : match.hourlyRateExpectation != null
          ? Number(match.hourlyRateExpectation)
          : null,
    npid: match.npid || null,
    serviceRole: match.serviceRole || "attendant",
    designation: "Care Attendant",
    isAvailable: true,
    adminApproved: true,
    verified: true,
    packages: match.packages && typeof match.packages === "object" ? match.packages : null,
    shiftPricing: match.shiftPricing && typeof match.shiftPricing === "object" ? match.shiftPricing : null,
  };
}
